import { useSnackbar } from 'notistack';
import { useMemo, useState, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import LockIcon from '@mui/icons-material/Lock';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Card,
  Chip,
  Paper,
  Stack,
  Button,
  MenuItem,
  Skeleton,
  IconButton,
  Typography,
  CardContent
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { NoPermissionSVG, EmptySubmissionSVG } from 'src/theme/illustrations';
import { SubmissionVisibilityStatus } from 'src/services/applications/applications.types';
import { useGetApplicationByIdQuery, useUpdateSubmissionVisibilityMutation, useUpdateAllSubmissionVisibilityMutation, useGetApplicationComplianceSubmissionsQuery } from 'src/services/applications/applications.service';

import { usePopover } from 'src/components/custom-popover';
import CustomPopover from 'src/components/custom-popover/custom-popover';

type Props = {
  applicationId: string;
  jobId: string;
};

export default function ApplicationScreeningSubmissionView({ applicationId, jobId }: Props) {

  const { t } = useTranslate();

  const authContext = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const [activeSubmissionId, setActiveSubmissionId] = useState<string | null>(null);
  const actionsPopover = usePopover();

  const { data: application } = useGetApplicationByIdQuery(
    applicationId as string,
    {
      skip: !applicationId,
    }
  );

  const { data: complianceSubmissions, isLoading, error } = useGetApplicationComplianceSubmissionsQuery(
    {
      applicationId,
      jobId,
    },
    {
      skip: !applicationId || !jobId,
    }
  );

  const [updateSubmissionVisibility, { isLoading: isUpdatingVisibility }] = useUpdateSubmissionVisibilityMutation();
  const [updateAllSubmissionVisibility, { isLoading: isUpdatingAllVisibility }] = useUpdateAllSubmissionVisibilityMutation();

  // Check if current user is the application manager
  const isManager = useMemo(() => application?.manager?.id === authContext.organization?.id, [application?.manager?.id, authContext.organization?.id]);

  const isAllShared = useMemo(() => {
    if (!complianceSubmissions || complianceSubmissions.length === 0) return false;
    return complianceSubmissions.every(submission => submission.visibility_status === SubmissionVisibilityStatus.SHARED);
  }, [complianceSubmissions]);

  const orderedQuestions = useMemo(() => {
    if (!complianceSubmissions) return [];

    return complianceSubmissions.slice().sort((a, b) => a.question.order - b.question.order);
  }, [complianceSubmissions]);

  /**
   * Sharing is reversible now (JT1-1491). It used to be one-way, which was liveable while the only
   * audience was a client organisation reading the application in the app. Guest stakeholders read
   * the same flag from a link, so a recruiter who shares an answer needs a way to take it back.
   */
  const handleSetVisibility = useCallback(async (submissionId: string, visibilityStatus: SubmissionVisibilityStatus) => {
    const sharing = visibilityStatus === SubmissionVisibilityStatus.SHARED;

    try {
      await updateSubmissionVisibility({
        applicationId,
        submissionId,
        visibilityStatus,
      }).unwrap();

      enqueueSnackbar(
        sharing
          ? t('applications.detail.compliance_submissions.share_success', 'Submission shared with client successfully')
          : t('applications.detail.compliance_submissions.hide_success', 'Submission hidden'),
        { variant: 'success' }
      );
      actionsPopover.onClose();
    } catch (err) {
      enqueueSnackbar(
        sharing
          ? t('applications.detail.compliance_submissions.share_error', 'Failed to share submission with client')
          : t('applications.detail.compliance_submissions.hide_error', 'Failed to hide submission'),
        { variant: 'error' }
      );
    }
  }, [applicationId, updateSubmissionVisibility, enqueueSnackbar, t, actionsPopover]);

  const handleShareAllWithClient = useCallback(async () => {
    try {
      await updateAllSubmissionVisibility({
        applicationId,
        visibilityStatus: SubmissionVisibilityStatus.SHARED,
      }).unwrap();

      enqueueSnackbar(t('applications.detail.compliance_submissions.share_all_success'), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(t('applications.detail.compliance_submissions.share_all_error'), {
        variant: 'error',
      });
    }
  }, [applicationId, updateAllSubmissionVisibility, enqueueSnackbar, t]);

  const handleOpenActions = useCallback((submissionId: string, event: React.MouseEvent<HTMLElement>) => {
    setActiveSubmissionId(submissionId);
    actionsPopover.onOpen(event);
  }, [actionsPopover]);

  const handleExportCSV = () => {
    if (!orderedQuestions || orderedQuestions.length === 0) return;

    // Prepare CSV headers
    const headers = ['Question', 'Required', 'Answer'];

    // Prepare CSV rows
    const rows = orderedQuestions.map((submission) => [
      `"${(submission.question.question.text || '').replace(/"/g, '""')}"`, // Escape quotes
      submission.question.required ? 'Yes' : 'No',
      `"${(submission.answer.text || '').replace(/"/g, '""')}"` // Escape quotes
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `screening-submissions-${applicationId}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="text" width={150} />
              <Skeleton variant="rectangular" height={80} sx={{ mt: 1 }} />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  // error state (403)
  if (error && typeof error === 'object' && 'status' in error && error.status === 403) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={4}>
            <NoPermissionSVG style={{ width: '180px', height: '180px' }} />
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockIcon fontSize="small" />
                {t('applications.detail.compliance_submissions.no_permission_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {t('applications.detail.compliance_submissions.no_permission_desc')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // empty state
  if (!complianceSubmissions || complianceSubmissions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={4}>
            <EmptySubmissionSVG style={{ width: '180px', height: '180px' }} />
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelpOutlineIcon fontSize="small" />
                {t('applications.detail.compliance_submissions.no_submissions_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {t('applications.detail.compliance_submissions.no_submissions_desc')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // data list display
  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="flex-end">
        <LoadingButton
          variant="contained"
          disabled={isAllShared}
          onClick={handleShareAllWithClient}
          size="small"
          loading={isUpdatingAllVisibility}
          sx={{ mr: 2 }}
        >
          {t('applications.detail.compliance_submissions.share_all_with_client')}
        </LoadingButton>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          size="small"
        >
          {t('common.export_csv', 'Export CSV')}
        </Button>
      </Box>
      {orderedQuestions.map((submission) => (
        <Paper
          key={submission.id}
          elevation={2}
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: '#fbfbfb',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {submission.question.question.text}
                {submission.question.required && (
                  <Typography
                    component="span"
                    color="error"
                    sx={{ ml: 0.5 }}
                  >
                    *
                  </Typography>
                )}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {
                  isManager && (
                    <>
                      <Chip
                        label={submission.visibility_status === SubmissionVisibilityStatus.SHARED ? t('common.shared') : t('common.hidden')}
                        size="small"
                        color={submission.visibility_status === SubmissionVisibilityStatus.SHARED ? 'success' : 'default'}
                        sx={{ fontWeight: 500 }}
                      />

                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenActions(submission.id, e)}
                        sx={{ ml: 0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
              </Box>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {submission.answer.text}
          </Typography>
        </Paper>
      ))}

      <CustomPopover
        open={actionsPopover.open}
        onClose={actionsPopover.onClose}
        arrow="right-top"
      >
        {activeSubmissionId && orderedQuestions.find(s => s.id === activeSubmissionId) && (
          <>
            <MenuItem
              onClick={() => handleSetVisibility(activeSubmissionId, SubmissionVisibilityStatus.SHARED)}
              disabled={
                orderedQuestions.find(s => s.id === activeSubmissionId)?.visibility_status === SubmissionVisibilityStatus.SHARED ||
                isUpdatingVisibility
              }
            >
              <ShareIcon sx={{ mr: 1 }} fontSize="small" />
              {t('applications.detail.compliance_submissions.share_with_client')}
            </MenuItem>

            <MenuItem
              onClick={() => handleSetVisibility(activeSubmissionId, SubmissionVisibilityStatus.HIDDEN)}
              disabled={
                orderedQuestions.find(s => s.id === activeSubmissionId)?.visibility_status !== SubmissionVisibilityStatus.SHARED ||
                isUpdatingVisibility
              }
            >
              <VisibilityOffIcon sx={{ mr: 1 }} fontSize="small" />
              {t('applications.detail.compliance_submissions.stop_sharing')}
            </MenuItem>
          </>
        )}
      </CustomPopover>
    </Stack>
  );
}